import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import DestinationCard from "@/components/DestinationCard";
import api from "@/lib/api";
import { Sparkles } from "lucide-react";

export default function Destinations() {
  const [destinations, setDestinations] = useState([]);
  const [comingSoon, setComingSoon] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/destinations"),
      api.get("/coming-soon"),
    ]).then(([d, cs]) => {
      setDestinations(d.data);
      setComingSoon(cs.data);
    }).finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16" data-testid="destinations-page">
      <div className="max-w-3xl mb-14">
        <p className="text-sm font-bold text-blue-600 uppercase tracking-widest mb-3">Destinations</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-[1.05]">
          Choose Your <span className="text-orange-500">Learning Destination</span>
        </h1>
        <p className="mt-5 text-slate-600 leading-relaxed">
          Discover programs across four active destinations and preview the new ones coming soon. Every destination offers workshops, cultural experiences, and international exposure tailored to student learners.
        </p>
      </div>

      {loading ? (
        <p className="text-slate-500">Loading…</p>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="destinations-grid">
          {destinations.map((d, i) => <DestinationCard key={d.id} dest={d} index={i} />)}
        </div>
      )}

      <div className="mt-24" data-testid="coming-soon-section">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-sm font-bold text-purple-600 uppercase tracking-widest mb-3">Coming Soon</p>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 leading-tight">
              More Countries Coming Soon
            </h2>
          </div>
          <span className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-orange-50 text-orange-700 text-xs font-semibold">
            <Sparkles className="w-3.5 h-3.5" /> New destinations added regularly
          </span>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {comingSoon.map((c, i) => (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: (i % 10) * 0.03 }}
              className="relative rounded-2xl overflow-hidden bg-white border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-transform"
              data-testid={`coming-soon-${c.id}`}
            >
              <div className="relative h-28 overflow-hidden">
                <img src={c.image} alt={c.name} loading="lazy" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent" />
              </div>
              <div className="p-3">
                <p className="text-xs font-bold text-slate-500">Coming Soon</p>
                <p className="font-display font-bold text-slate-900 leading-tight text-sm">{c.name}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <p className="mt-6 text-sm text-slate-500 italic">New destinations and educational experiences are being added regularly.</p>
      </div>
    </div>
  );
}
